import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Container, Text, Title } from "@mantine/core";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/navbar/navbar";
import Loader from "@/components/shared/loader";
import PatientInfoForm, {
  PatientInfoFormHandle,
} from "@/components/patient-info-form/patient-info-form";
import { PageFooter } from "@/components/layout/page-footer";
import styles from "./refer-patient-info.module.scss";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";

const GET_APPEAL_BY_ID = gql(`
  query GetAppealByIdForRedirect($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
    }
  }
`);

export {
  patientInfoFormSchema,
  VALIDATION_MESSAGES,
  PHONE_REGEX,
  MAX_AGE_YEARS,
  MIN_AGE_YEARS,
} from "@/components/patient-info-form/patient-info-form";

function ReferPatientInfo() {
  const { appealId } = useParams<{ appealId: string }>();
  const navigate = useNavigate();
  const formRef = useRef<PatientInfoFormHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: _appealQueryData,
    loading: appealQueryLoading,
    error: appealQueryError,
  } = useQuery(GET_APPEAL_BY_ID, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!appealId || appealQueryError) {
      navigate("/");
    }
  }, [appealId, appealQueryError, navigate]);

  if (!appealId) {
    return null;
  }

  if (appealQueryLoading) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <Container fluid className={styles.mainContainer}>
          <div className={styles.loadingContainer}>
            <Loader color="loader_almost_black" width="36" height="36" />
          </div>
        </Container>
      </Navbar>
    );
  }

  if (appealQueryError) {
    return null;
  }

  const handleFooterSubmit = async () => {
    setIsSubmitting(true);
    try {
      const success = await formRef.current?.submit();
      if (success) {
        navigate(`/appeals/${appealId}/strengthen/insurance`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <PageFooter
      primaryButton={{
        text: "Next",
        onClick: handleFooterSubmit,
        loading: isSubmitting,
        disabled: isSubmitting,
      }}
    />
  );

  return (
    <Navbar
      titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      footer={footer}
    >
      <Container fluid className={styles.mainContainer}>
        <div className={styles.pageHeader}>
          <Title order={2} className={styles.patientInfoHeading}>
            Patient Info
          </Title>
          <Text className={styles.subtitle}>
            Provide required information to start a referral
          </Text>
        </div>
        <PatientInfoForm ref={formRef} appealId={appealId} />
      </Container>
    </Navbar>
  );
}

export default ReferPatientInfo;
